// home/HeroSection.jsx
import React from 'react';
import { ChevronRight, Calculator } from 'lucide-react';
import { scrollToSection } from '../../utils/scrollToSection';

function IntelligenceGraphic() {
  return (
    <svg viewBox="0 0 400 400" className="w-full max-w-md mx-auto" aria-hidden="true">
      <defs>
        <radialGradient id="glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="hsl(217, 91%, 60%)" stopOpacity="0.3" />
          <stop offset="100%" stopColor="hsl(217, 91%, 60%)" stopOpacity="0" />
        </radialGradient>
      </defs>
      {/* Ambient glow */}
      <circle cx="200" cy="200" r="160" fill="url(#glow)" />
      {/* Outer ring */}
      <circle cx="200" cy="200" r="140" fill="none" stroke="hsl(217, 91%, 60%)" strokeWidth="1" strokeOpacity="0.2" />
      {/* Middle ring */}
      <circle cx="200" cy="200" r="95" fill="none" stroke="hsl(217, 91%, 60%)" strokeWidth="1" strokeOpacity="0.3">
        <animateTransform attributeName="transform" type="rotate" from="0 200 200" to="360 200 200" dur="60s" repeatCount="indefinite" />
      </circle>
      {/* Inner ring */}
      <circle cx="200" cy="200" r="50" fill="none" stroke="hsl(25, 90%, 55%)" strokeWidth="1.5" strokeOpacity="0.5" />
      {/* Core node */}
      <circle cx="200" cy="200" r="20" fill="hsl(217, 91%, 60%)" fillOpacity="0.15" stroke="hsl(217, 91%, 60%)" strokeWidth="2">
        <animate attributeName="r" values="18;22;18" dur="3s" repeatCount="indefinite" />
      </circle>
      <text x="200" y="205" textAnchor="middle" fill="hsl(217, 91%, 60%)" fontSize="12" fontWeight="700" fontFamily="sans-serif">KG</text>
      {/* Sensor nodes on outer ring */}
      {[0, 72, 144, 216, 288].map((angle, i) => {
        const rad = (angle * Math.PI) / 180;
        const x = 200 + 140 * Math.cos(rad);
        const y = 200 + 140 * Math.sin(rad);
        return (
          <g key={i}>
            <line x1="200" y1="200" x2={x} y2={y} stroke="hsl(217, 91%, 60%)" strokeWidth="0.5" strokeOpacity="0.15" />
            <circle cx={x} cy={y} r="6" fill="hsl(220, 15%, 15%)" stroke="hsl(217, 91%, 60%)" strokeWidth="1.5" strokeOpacity="0.6">
              <animate attributeName="stroke-opacity" values="0.3;0.8;0.3" dur={`${2 + i * 0.5}s`} repeatCount="indefinite" />
            </circle>
          </g>
        );
      })}
      {/* Sensor nodes on middle ring */}
      {[30, 150, 270].map((angle, i) => {
        const rad = (angle * Math.PI) / 180;
        const x = 200 + 95 * Math.cos(rad);
        const y = 200 + 95 * Math.sin(rad);
        return (
          <g key={`mid-${i}`}>
            <line x1="200" y1="200" x2={x} y2={y} stroke="hsl(25, 90%, 55%)" strokeWidth="0.5" strokeOpacity="0.2" />
            <circle cx={x} cy={y} r="5" fill="hsl(220, 15%, 15%)" stroke="hsl(25, 90%, 55%)" strokeWidth="1.5" strokeOpacity="0.5">
              <animate attributeName="stroke-opacity" values="0.3;0.7;0.3" dur={`${2.5 + i * 0.7}s`} repeatCount="indefinite" />
            </circle>
          </g>
        );
      })}
    </svg>
  );
}

export function HeroSection() {
  return (
    <div id="hero" className="relative overflow-hidden bg-background">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center py-16 md:py-24 lg:py-32">
          {/* Left: Content */}
          <div className="sm:text-center lg:text-left">
            <h1 className="text-4xl font-bold tracking-tight text-foreground sm:text-5xl md:text-6xl lg:text-7xl" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <span className="block">The Operating System</span>
              <span className="block text-primary">for the Autonomous SOC</span>
            </h1>
            <p className="mt-6 text-lg text-muted-foreground sm:mx-auto sm:max-w-xl lg:mx-0 lg:max-w-lg">
              Your cameras are just sensors. Kinetic Guard is the vendor-agnostic intelligence layer that transforms any video feed into a proactive, autonomous security operation.
            </p>
            <div className="mt-8 sm:flex sm:justify-center lg:justify-start gap-4">
              <button
                onClick={() => scrollToSection('audit')}
                className="flex w-full sm:w-auto items-center justify-center rounded-md bg-primary px-8 py-3 text-base font-medium text-primary-foreground hover:bg-primary/90 transition-colors md:py-4 md:px-10 md:text-lg"
              >
                Start Your Readiness Audit
                <ChevronRight className="ml-2 h-5 w-5" />
              </button>
              <button
                onClick={() => scrollToSection('calculator')}
                className="mt-3 sm:mt-0 flex w-full sm:w-auto items-center justify-center rounded-md border border-primary bg-transparent px-8 py-3 text-base font-medium text-primary hover:bg-primary/10 transition-colors md:py-4 md:px-10 md:text-lg"
              >
                <Calculator className="mr-2 h-5 w-5" />
                Calculate Labor Waste
              </button>
            </div>
          </div>

          {/* Right: Abstract Intelligence Graphic */}
          <div className="hidden lg:block">
            <IntelligenceGraphic />
          </div>
        </div>
      </div>
    </div>
  );
}
